import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { httpService } from '@app/modules/common/http';
import { syncService } from '@app/modules/sync';

/**
 * Fichaje (Denpora) hecho SIN conexión, pendiente de enviar.
 *
 * Misma idea que la cola del kiosco de Denpora (offline-clock-queue.service.ts):
 * se guarda en el TPV con SU hora real (`at`) y se manda al recuperar la conexión
 * con `occurred_at`, que el servidor de Denpora acepta (ventana: 24 h hacia atrás,
 * nada en el futuro). Un fichaje de las 12:00 enviado a las 17:00 queda a las 12:00.
 *
 * La credencial (PIN o pulsera) viaja con el evento y la revalida NUESTRO servidor
 * al enviar (denpora/clock.php → staffident), igual que en Denpora: el cliente no
 * tiene hashes, así que sin conexión no se sabe QUIÉN ficha; la identidad la decide
 * el servidor, también para lo encolado. Por eso el modal pregunta ENTRADA o SALIDA
 * al fichar sin red: `expected` es obligatorio en un fichaje diferido (Denpora deja
 * de alternar y hace exactamente lo que fichó el empleado, o nada: 409). Una
 * elección equivocada NUNCA registra lo contrario: acaba en 409 y se avisa.
 */
export interface QueuedClock {
    id: string;
    place: any;                             // objid del local (string en el modelo)
    user?: any;                             // objid del USER si el servidor ya lo identificó (Denpora caído)
    pin?: string;
    rfid?: string;
    at: string;                             // ISO 8601, hora real del fichaje
    expected: 'clock_in' | 'clock_out';
    tried?: boolean;                        // ya salió hacia el servidor al menos una vez
    unsaved?: boolean;                      // solo en el retorno de Enqueue: no se pudo escribir en localStorage
}

/** Resultado de un envío, para que la vista lo cuente (toast). */
export interface QueuedClockResult {
    event: QueuedClock;                     // SIN credencial
    ok: boolean;
    clock?: any;                            // { action, employee_name, time } si ok
    reason?: string;                        // motivo de denpora/clock.php si no ok
    message?: string;                       // texto de Denpora si lo hay
}

const STORAGE_KEY = 'unpispas_denpora_clock_queue';
const RESULTS_KEY = 'unpispas_denpora_clock_results';   // avisos aún no enseñados (sin credencial)
const LAST_KEY    = 'unpispas_denpora_last_clock';      // último fichaje visto en ESTE TPV por usuario
const LOCK_NAME   = 'unpispas_denpora_clock_flush';     // una sola ventana envía la cola a la vez
const MAX_EVENTS  = 50;                     // un corte largo cabe de sobra; evita llenar localStorage
const MAX_RESULTS = 20;
const RETRY_MS    = 2 * 60 * 1000;          // reintento periódico mientras haya pendientes (Denpora caído con nuestra red OK)
const WINDOW_MS   = 24 * 3600 * 1000;       // ventana de Denpora para un fichaje diferido (domain::deferred_clock)
const SUGGEST_MS  = 20 * 3600 * 1000;       // un último fichaje más viejo no sirve para sugerir

/**
 * Motivos de denpora/clock.php que son DEFINITIVOS: reintentar daría lo mismo, así
 * que el fichaje se descarta y se avisa. Todo lo demás (sin red, Denpora caído o
 * 5xx -> 'offline', límite de intentos de PIN -> 'ratelimit', token revocado o
 * integración desactivada -> lo arregla un encargado, sesión caducada / errores
 * sin motivo) se CONSERVA y se reintenta: es un registro obligatorio y el
 * camarero vio que se guardaba. Lo que no llegue a tiempo acaba en 'hour' (24 h).
 */
const DEFINITIVE = [ 'pin', 'rfid', 'noemail', 'notfound', 'hour', 'conflict', 'denpora' ];

@Injectable({ providedIn: 'root' })
export class denporaClockQueueService {
    private _queue: QueuedClock[] = this._readQueue();
    private _memoryOnly = new Set<string>();    // encolados aquí sin poder escribirlos: no están en localStorage
    private _dropped = new Set<string>();   // ya resueltos aquí: si no se pudo escribir, siguen en localStorage
    private _pending = new BehaviorSubject<number>(this._queue.length);
    public OnPending = this._pending.asObservable();

    // Avisos: se guardan (también en localStorage) hasta que una vista los RECOGE con
    // TakeResults(). OnResult solo avisa de que hay algo nuevo: si la vista del local
    // no existe en ese momento, se enseñan cuando vuelva (ngOnInit).
    private _undelivered: QueuedClockResult[] = this._readJson(RESULTS_KEY, []);
    private _onResult = new Subject<void>();
    public OnResult = this._onResult.asObservable();

    private _last: { [user: string]: { action: string, at: string } } = this._readJson(LAST_KEY, {});

    private _inflight: Promise<void> = null;
    private _online_subscription: Subscription = null;
    private _retry = null;

    constructor(private sync: syncService, private http: httpService){
        // Al volver la red se vacía sola; es el disparador principal.
        this._online_subscription = this.http.OnOnline.subscribe(online => {
            if (online){
                this.Flush();
            }
        });
        // localStorage es compartido entre ventanas: lo que encole o envíe otra ventana
        // del mismo TPV se ve aquí (si no, esta lo pisaría al escribir su copia).
        if (typeof window !== 'undefined'){
            window.addEventListener('storage', (e: StorageEvent) => {
                if ((e.key === STORAGE_KEY) || (e.key === null)){
                    this._reload();
                    this._pending.next(this._queue.length);
                    this._armRetry();
                }
            });
        }
        this._armRetry();
    }

    get Count(): number {
        return this._queue.length;
    }

    /** Pendientes de UN local (el orden solo importa dentro de cada local). */
    CountFor(place: any): number {
        return this._queue.filter(e => String(e.place) === String(place)).length;
    }

    /** Guarda un fichaje para enviarlo cuando haya conexión. */
    Enqueue(event: { place: any, user?: any, pin?: string, rfid?: string, expected: 'clock_in' | 'clock_out' }): QueuedClock {
        let _item: QueuedClock = {
            id: this._newId(),
            place: event.place,
            at: new Date().toISOString(),
            expected: event.expected
        };
        if (event.user){ _item.user = event.user; }
        if (event.rfid){ _item.rfid = event.rfid; }
        else { _item.pin = event.pin; }

        this._reload();
        this._queue.push(_item);
        // Tope: se pierde lo MÁS ANTIGUO, y no en silencio (registro de jornada).
        while (this._queue.length > MAX_EVENTS){
            let _lost = this._queue.shift();
            console.error('[DENPORA-QUEUE] cola llena: fichaje descartado', this._public(_lost));
            this._publish({ event: this._public(_lost), ok: false, reason: 'full' });
        }
        if (_item.user){
            this.NoteClock(_item.user, _item.expected, _item.at);
        }
        let _saved = this._persist();
        this._armRetry();
        let _public = this._public(_item);
        if (!_saved){
            this._memoryOnly.add(_item.id);
            console.error('[DENPORA-QUEUE] fichaje solo en memoria: no se pudo escribir en localStorage', _public);
            _public.unsaved = true;
        }
        return _public;
    }

    /**
     * Envía lo pendiente, EN ORDEN DENTRO DE CADA LOCAL (una salida no puede llegar
     * antes que su entrada). Cada local es independiente (su propio token e
     * integración en Denpora): un fallo recuperable corta SOLO la cola de ese local
     * y el resto sigue; un rechazo definitivo (ver DEFINITIVE) descarta ESE fichaje,
     * lo avisa y sigue.
     * El modal de fichaje espera a esto antes de fichar en vivo: si queda algo, el
     * fichaje nuevo va DETRÁS en la cola (nunca se adelanta a uno pendiente).
     */
    Flush(): Promise<void> {
        this._reload();
        if (!this._queue.length || this.http.IsOnline === false){
            return Promise.resolve();
        }
        // Un envío en curso se comparte, no se duplica (dos disparadores a la vez
        // mandarían el mismo fichaje dos veces).
        if (!this._inflight){
            this._inflight = this._withLock(() => this._run()).catch(() => {}).then(() => { this._inflight = null; });
        }
        return this._inflight;
    }

    /** Avisos pendientes de enseñar (se borran al recogerlos). */
    TakeResults(): QueuedClockResult[] {
        let _out = this._undelivered;
        this._undelivered = [];
        this._writeJson(RESULTS_KEY, null);
        return _out;
    }

    /** Último fichaje de este usuario visto en ESTE TPV (en vivo, encolado o enviado). */
    NoteClock(user: any, action: string, at?: string){
        if (!user || ((action !== 'clock_in') && (action !== 'clock_out'))){
            return;
        }
        this._last[String(user)] = { action: action, at: at || new Date().toISOString() };
        this._writeJson(LAST_KEY, this._last);
    }

    /**
     * Sugerencia para un fichaje sin conexión: lo CONTRARIO del último fichaje de
     * este usuario en este TPV, si es reciente. null si no se sabe (no se adivina).
     * Es solo una ayuda: fichajes hechos en otro dispositivo no se ven aquí.
     */
    Suggest(user: any): { expected: 'clock_in' | 'clock_out', last: { action: string, at: string } } {
        let _l = user ? this._last[String(user)] : null;
        if (!_l || isNaN(Date.parse(_l.at)) || ((Date.now() - Date.parse(_l.at)) > SUGGEST_MS)){
            return null;
        }
        return { expected: (_l.action === 'clock_in') ? 'clock_out' : 'clock_in', last: _l };
    }

    /**
     * Una sola ventana envía a la vez: dos enviando lo mismo darían un 'conflict' falso
     * en la segunda (Denpora ya lo tiene). Si otra ventana lo tiene, esta no envía: lo
     * que quede se recoge en el siguiente Flush. Sin Web Locks (o si fallan) se envía igual.
     */
    private _withLock(run: () => Promise<void>): Promise<void> {
        let _locks = (typeof navigator !== 'undefined') ? (navigator as any).locks : null;
        if (!_locks){
            return run();
        }
        let _started = false;
        return _locks.request(LOCK_NAME, { ifAvailable: true }, (lock: any) => {
            if (!lock){
                return undefined;
            }
            _started = true;
            return run();
        }).catch((e: any) => {
            if (_started){
                throw e;
            }
            return run();
        });
    }

    private async _run(): Promise<void> {
        // Locales en el orden en que aparecen en la cola (lo encolado durante el envío
        // se recoge en el siguiente Flush o en la siguiente vuelta de su local).
        let _places: string[] = [];
        for (let _e of this._queue){
            if (_places.indexOf(String(_e.place)) === -1){
                _places.push(String(_e.place));
            }
        }
        for (let _place of _places){
            await this._runPlace(_place);
        }
    }

    private async _runPlace(place: string): Promise<void> {
        while (true){
            this._reload();     // otra ventana puede haberlo enviado ya
            let _item = this._queue.find(e => String(e.place) === place);
            if (!_item){
                return;
            }

            // Fuera de la ventana de Denpora ya no se puede registrar: se descarta y se
            // avisa (se corrige por solicitud de jornada en Denpora), sin bloquear la cola.
            if ((Date.now() - Date.parse(_item.at)) > WINDOW_MS){
                this._reject(_item, 'hour', null);
                continue;
            }

            // Marca ANTES de enviar: si la app muere con la petición en vuelo, al volver
            // se sabe que pudo llegar. Con expected_action el reintento es seguro: si el
            // primero se aplicó, Denpora responde 409 en vez de fichar lo contrario.
            _item.tried = true;
            this._persist();

            let _body: any = { occurred_at: _item.at, expected_action: _item.expected };
            if (_item.rfid){ _body.rfid = _item.rfid; }
            else { _body.pin = _item.pin; }

            let _data: any = null;
            try {
                _data = await this.sync.DoRequest('denpora/clock.php', { place: _item.place }, JSON.stringify(_body), false);
            }
            catch(e){
                return;     // sin conexión con nuestro servidor: se reintenta más tarde
                            // (los demás locales lo intentan igual: también fallarán rápido)
            }

            if (_data && (_data['errorcode'] == 0) && _data['clock']){
                this._drop(_item);
                this.NoteClock(_data['user'], _data['clock']['action'], _item.at);
                this._publish({ event: this._public(_item), ok: true, clock: _data['clock'] });
                continue;
            }

            let _reason = _data ? _data['reason'] : null;
            if (!_reason || (DEFINITIVE.indexOf(_reason) == -1)){
                // Recuperable (Denpora caído/5xx, límite de PIN, token o integración a
                // revisar, sesión caducada, error sin motivo): se conserva TODO lo de
                // ESTE local y se corta aquí para no romper su orden. Los demás locales
                // siguen. Se reintenta más tarde.
                console.warn('[DENPORA-QUEUE] envío aplazado en el local ' + place + ' (' + (_reason || ('errorcode ' + (_data ? _data['errorcode'] : '?'))) + '), quedan ' + this.CountFor(place));
                return;
            }
            // 'conflict' (409 con expected_action): Denpora no lo registra porque su
            // estado no coincide con lo fichado (ya constaba, o se eligió lo contrario).
            // Nunca se ficha lo contrario: se descarta y se avisa para revisarlo.
            this._reject(_item, _reason, _data['message']);
        }
    }

    private _reject(item: QueuedClock, reason: string, message: string){
        this._drop(item);
        console.error('[DENPORA-QUEUE] fichaje pendiente NO registrado (' + reason + ')', this._public(item));
        this._publish({ event: this._public(item), ok: false, reason: reason, message: message });
    }

    private _publish(result: QueuedClockResult){
        this._undelivered = this._undelivered.concat([ result ]).slice(-MAX_RESULTS);
        this._writeJson(RESULTS_KEY, this._undelivered);
        this._onResult.next();
    }

    private _drop(item: QueuedClock){
        this._dropped.add(item.id);
        this._memoryOnly.delete(item.id);
        this._reload();
        this._queue = this._queue.filter(e => e.id !== item.id);
        this._persist();
    }

    /** Copia SIN credencial: lo que sale a logs/toasts/resultados. */
    private _public(item: QueuedClock): QueuedClock {
        let { pin, rfid, ...rest } = item;
        return rest as QueuedClock;
    }

    private _armRetry(){
        if (this._retry || !this._queue.length){
            return;
        }
        this._retry = setInterval(() => {
            if (!this._queue.length){
                clearInterval(this._retry);
                this._retry = null;
                return;
            }
            this.Flush();
        }, RETRY_MS);
    }

    private _persist(): boolean {
        this._pending.next(this._queue.length);
        let _saved = this._writeJson(STORAGE_KEY, this._queue.length ? this._queue : null);
        if (_saved){
            this._memoryOnly.clear();
        }
        return _saved;
    }

    /**
     * Relee la cola antes de cambiarla: otra ventana puede haber encolado o enviado.
     * Si localStorage no se puede leer manda la memoria. Lo demás lo decide lo guardado,
     * salvo lo que nunca se pudo escribir: lo que falte de lo ya guardado lo ha resuelto
     * otra ventana.
     */
    private _reload(){
        try {
            localStorage.getItem(STORAGE_KEY);
        }
        catch(e){
            return;
        }
        let _stored = this._readQueue().filter(e => !this._dropped.has(e.id));
        let _ids = _stored.map(e => e.id);
        let _local = this._queue.filter(e => this._memoryOnly.has(e.id) && (_ids.indexOf(e.id) === -1));
        if (_local.length){
            _stored = _stored.concat(_local);
            _stored.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
        }
        this._queue = _stored;
    }

    private _writeJson(key: string, value: any): boolean {
        try {
            if (value === null){
                localStorage.removeItem(key);
            }
            else {
                localStorage.setItem(key, JSON.stringify(value));
            }
            return true;
        }
        catch(e){
            // sin sitio / bloqueado: sigue vivo en memoria en esta sesión
            return false;
        }
    }

    private _readJson(key: string, fallback: any): any {
        try {
            let _raw = JSON.parse(localStorage.getItem(key) || 'null');
            if ((_raw === null) || (typeof _raw !== typeof fallback) || (Array.isArray(_raw) !== Array.isArray(fallback))){
                return fallback;
            }
            return _raw;
        }
        catch(e){
            return fallback;
        }
    }

    private _readQueue(): QueuedClock[] {
        let _raw = this._readJson(STORAGE_KEY, []);
        // Entrada a entrada: una corrupta no se lleva por delante las válidas.
        return _raw.filter((e: any) => e && (typeof e.id === 'string') && ((typeof e.place === 'string') || (typeof e.place === 'number')) &&
            (typeof e.at === 'string') && !isNaN(Date.parse(e.at)) &&
            ((e.expected === 'clock_in') || (e.expected === 'clock_out')) &&
            ((typeof e.pin === 'string' && e.pin.length > 0) || (typeof e.rfid === 'string' && e.rfid.length > 0)));
    }

    private _newId(): string {
        try {
            if ((typeof crypto !== 'undefined') && crypto.randomUUID){
                return crypto.randomUUID();
            }
        } catch(e){}
        return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
    }
}
